import React from 'react'
import { DndContext, DragEndEvent, DragOverEvent, DragOverlay, DragStartEvent, SensorDescriptor, SensorOptions, closestCenter } from '@dnd-kit/core'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { ProfileTableHeaderCell } from './ProfileTableHeader'
import { ProfileTableDragGhost } from './ProfileTableDragGhost'
import type { FixedColumnSort, ProfileColumnDef, SelectionSort } from './types'
import { PROFILE_TABLE_VARIANT_STYLES } from './variantStyles'

interface ProfileTableHeaderRowProps<TFixedKey extends string, TMiddleKey extends string> {
    uiVariant?: keyof typeof PROFILE_TABLE_VARIANT_STYLES
    sensors: SensorDescriptor<SensorOptions>[]
    visibleColumns: ProfileColumnDef<TFixedKey | TMiddleKey>[]
    fixedColumns: ProfileColumnDef<TFixedKey>[]
    sortableItems: string[]
    sortableColumns: TMiddleKey[]
    activeSortColumn: TMiddleKey | null
    sortDirection: 'asc' | 'desc'
    fixedColumnKeys: Set<string>
    addableColumns: Array<{ key: TMiddleKey; label: string }>
    visibleHideableColumnsCount: number
    activeColumnItem: ProfileColumnDef<TFixedKey | TMiddleKey> | null
    resizingColumn: TMiddleKey | null
    allSelected: boolean
    someSelected: boolean
    accentColor: string
    hoverAccent: string
    getHeaderCellStyle: (column: ProfileColumnDef<string>) => React.CSSProperties
    getColumnWidth: (key: TFixedKey | TMiddleKey) => number
    isColumnCollapsed: (key: TFixedKey | TMiddleKey) => boolean
    toggleCollapse: (key: TMiddleKey) => void
    hideColumn: (key: TMiddleKey) => void
    addColumnToRight: (anchor: TMiddleKey, columnToAdd: TMiddleKey) => void
    onSelectAll?: (checked: boolean) => void
    onSortChange?: (column: TMiddleKey) => void
    selectionSort?: SelectionSort
    fixedColumnSorts?: Partial<Record<TFixedKey, FixedColumnSort>>
    getStickyOffset: (key: TFixedKey | TMiddleKey) => number
    handleResizeStart: (event: React.MouseEvent, column: TMiddleKey) => void
    handleDragStart: (event: DragStartEvent) => void
    handleDragOver: (event: DragOverEvent) => void
    handleDragEnd: (event: DragEndEvent) => void
    clearDragState: () => void
    selectionColumnKey?: string
}

/**
 * Renders the `<tr>` row inside `<thead>` — including the DnD context and
 * every `ProfileTableHeaderCell` for fixed and middle columns.
 */
export const ProfileTableHeaderRow = <TFixedKey extends string, TMiddleKey extends string>({
    uiVariant = 'default',
    sensors,
    visibleColumns,
    sortableItems,
    sortableColumns,
    activeSortColumn,
    sortDirection,
    fixedColumnKeys,
    addableColumns,
    visibleHideableColumnsCount,
    activeColumnItem,
    resizingColumn,
    allSelected,
    someSelected,
    accentColor,
    hoverAccent,
    getHeaderCellStyle,
    getColumnWidth,
    isColumnCollapsed,
    toggleCollapse,
    hideColumn,
    addColumnToRight,
    onSelectAll,
    onSortChange,
    selectionSort,
    fixedColumnSorts,
    getStickyOffset,
    handleResizeStart,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    clearDragState,
    selectionColumnKey,
}: ProfileTableHeaderRowProps<TFixedKey, TMiddleKey>) => (
    <tr className={PROFILE_TABLE_VARIANT_STYLES[uiVariant].headerRow}>
        {/* dnd-kit renders its screen-reader text as <div>s, which are invalid inside a <tr>; portal them out */}
        <DndContext
            accessibility={{ container: document.body }}
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={clearDragState}
        >
            <SortableContext items={sortableItems} strategy={horizontalListSortingStrategy}>
                {visibleColumns.map((column) => {
                    const collapsed = isColumnCollapsed(column.key as TFixedKey | TMiddleKey)
                    const isMiddleColumn = !fixedColumnKeys.has(column.key)
                    const baseStyle = getHeaderCellStyle(column as ProfileColumnDef<string>)
                    const middleKey = column.key as TMiddleKey
                    const isSortableColumn = isMiddleColumn && sortableColumns.includes(middleKey)
                    const isSelectionSort = !!selectionSort && column.key === selectionColumnKey
                    const fixedSort = !isMiddleColumn && !isSelectionSort ? fixedColumnSorts?.[column.key as TFixedKey] : undefined
                    const showSortTrigger = (isSortableColumn || isSelectionSort || !!fixedSort) && !collapsed
                    const resolvedSortDirection: 'asc' | 'desc' | null = isSelectionSort
                        ? selectionSort.direction
                        : fixedSort
                          ? fixedSort.direction
                          : isSortableColumn && activeSortColumn === middleKey
                            ? sortDirection
                            : null

                    return (
                        <ProfileTableHeaderCell
                            key={column.key}
                            uiVariant={uiVariant}
                            column={column}
                            collapsed={collapsed}
                            isMiddleColumn={isMiddleColumn}
                            baseStyle={baseStyle}
                            addableColumns={addableColumns}
                            hideDisabled={visibleHideableColumnsCount <= 1}
                            onToggleCollapse={() => toggleCollapse(column.key as TMiddleKey)}
                            onAddToRight={(columnToAdd) => addColumnToRight(column.key as TMiddleKey, columnToAdd as TMiddleKey)}
                            onHide={() => hideColumn(column.key as TMiddleKey)}
                            allSelected={allSelected}
                            someSelected={someSelected}
                            onSelectAll={onSelectAll}
                            onResizeStart={(event) => handleResizeStart(event, column.key as TMiddleKey)}
                            isResizing={resizingColumn === column.key}
                            hoverAccent={hoverAccent}
                            showSortTrigger={showSortTrigger}
                            sortDirection={resolvedSortDirection}
                            sortLabel={isSelectionSort ? selectionSort.label : undefined}
                            onSortTrigger={
                                isSelectionSort
                                    ? selectionSort.onToggle
                                    : fixedSort
                                      ? fixedSort.onToggle
                                      : isSortableColumn
                                        ? () => onSortChange?.(middleKey)
                                        : undefined
                            }
                            stickyOffset={getStickyOffset(column.key as TFixedKey | TMiddleKey)}
                            accentColor={accentColor}
                            selectionColumnKey={selectionColumnKey}
                        />
                    )
                })}
            </SortableContext>

            <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
                {activeColumnItem ? (
                    <ProfileTableDragGhost
                        activeColumnItem={activeColumnItem as ProfileColumnDef<string>}
                        width={getColumnWidth(activeColumnItem.key as TFixedKey | TMiddleKey)}
                        uiVariant={uiVariant}
                    />
                ) : null}
            </DragOverlay>
        </DndContext>
    </tr>
)
